import "./ui.css";

/* Pure geometry, kept next to its only consumer. Lived in
   pages/company/dashboard/dashboardData.ts until the sparkline was promoted out
   of that page; a shared primitive must not import from a page module. */
export function sparklinePoints(
	values: number[],
	width = 100,
	height = 24,
): string {
	if (values.length < 2) return "";
	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min;
	const lastIndex = values.length - 1;
	return values
		.map((value, i) => {
			const x = (i / lastIndex) * width;
			const ratio = range === 0 ? 0.5 : (value - min) / range;
			const y = height - ratio * height;
			return `${x},${y}`;
		})
		.join(" ");
}

interface SparklineProps {
	values: number[];
	/* Semantic, not decorative: "neg" is the error token. A sparkline is data,
	   so it takes its colour from the semantic palette, not the chart series. */
	tone?: "pos" | "neg";
	width?: number;
	height?: number;
}

/* aria-hidden on purpose: the trend is always accompanied by its own figure in
   the surrounding StatNumber, so announcing the polyline would be noise. If you
   ever use this without a visible value, give it a label instead. */
export function Sparkline({
	values,
	tone = "pos",
	width = 100,
	height = 24,
}: SparklineProps) {
	if (values.length < 2) return null;
	return (
		<svg
			aria-hidden="true"
			width="100%"
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			preserveAspectRatio="none"
			className="ui-sparkline"
		>
			<polyline
				points={sparklinePoints(values, width, height)}
				fill="none"
				stroke={tone === "neg" ? "var(--color-error)" : "var(--color-primary)"}
				strokeWidth={2}
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
	);
}
